// Agents › a delegation request (`/c/{slug}/agents/delegations/{id}`; spec 06 "delegation requests
// render as cards (who, what, budget, deadline, Accept/Decline)"; 05 §Delegation rules; M3-08):
// a run handed work to another seat or member. The recipient (or a recipient DE's supervisor)
// accepts or declines it; once accepted, whoever does the work hands it back (Complete, with a
// summary). Reached from the wake-up log, a delegated run's "woken by", and the inbox.
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  PageHeader,
  SettingsSection,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckIcon, SendIcon, XIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { NotFoundPage } from "../../pages/status-pages";
import type { CompanyAccess } from "../company";
import { problemText } from "../de/de-status";
import { formatDateTime } from "../format";
import { Facts, LoadError, Loading, Section } from "../screen-parts";
import { formatMicros, useCurrency } from "./agent-parts";
import { agentKeys, invalidateAgents } from "./agent-queries";
import { useDeNames } from "./runs-screen";

type Delegation = Awaited<ReturnType<Api["client"]["delegationRequests"]["get"]>>;

const STATES: Record<
  Delegation["state"],
  [string, "warning" | "info" | "danger" | "neutral" | "success"]
> = {
  sent: ["Waiting for an answer", "warning"],
  accepted: ["Accepted", "info"],
  declined: ["Declined", "danger"],
  expired: ["Expired", "neutral"],
  completed: ["Handed back", "success"],
};

export const delegationQuery = (api: Api, co: string, id: string) => ({
  queryKey: [...agentKeys.runs(co), "delegation", id] as const,
  queryFn: () => api.client.delegationRequests.get({ params: { co, id } }),
  retry: false,
});

export function DelegationScreen({
  api,
  company,
  id,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
}) {
  const q = useQuery(delegationQuery(api, company.id, id));
  const names = useDeNames(api, company.id);
  const currency = useCurrency(api, company.id);
  const qc = useQueryClient();
  const accept = useMutation({
    mutationFn: () => api.client.delegationRequests.accept({ params: { co: company.id, id } }),
    onSettled: () => invalidateAgents(qc, company.id),
  });
  if (q.isError && q.error instanceof ApiError && q.error.status === 404)
    return <NotFoundPage message="No such delegation request, or you may not view it." />;
  const d = q.data;
  const who = (m: string) => names.get(m) ?? "A member";
  return (
    <>
      <PageHeader
        eyebrow={
          <Link
            to="/c/$slug/agents/activity"
            params={{ slug: company.slug }}
            search={(prev: Record<string, unknown>) => ({
              ...keepFocus(prev),
              state: "wakeups" as const,
            })}
            className="underline-offset-4 hover:underline"
          >
            Wake-ups
          </Link>
        }
        title={d ? `${who(d.from_membership_id)} → ${who(d.to_membership_id)}` : "Delegation"}
        badge={
          d ? (
            <Badge variant={STATES[d.state][1]} data-delegation-state={d.state}>
              {STATES[d.state][0]}
            </Badge>
          ) : null
        }
        subtitle="Delegated work: who asked whom, with what budget and by when."
        actions={
          d?.state === "sent" ? (
            <>
              <Button size="lg" disabled={accept.isPending} onClick={() => accept.mutate()}>
                <CheckIcon aria-hidden="true" />
                {accept.isPending ? "Accepting…" : "Accept"}
              </Button>
              <AnswerDialog api={api} company={company} id={id} kind="decline" />
            </>
          ) : d?.state === "accepted" ? (
            <AnswerDialog api={api} company={company} id={id} kind="complete" />
          ) : null
        }
      />
      {accept.isError ? (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {problemText(accept.error, "The request could not be accepted.")}
        </p>
      ) : null}
      {q.isPending ? (
        <Loading label="Loading the delegation" rows={3} />
      ) : q.isError ? (
        <LoadError what="The delegation" error={q.error} />
      ) : d ? (
        <>
          <SettingsSection title="Request" icon={SendIcon} data-slot="delegation-facts">
            <Facts
              items={[
                { label: "From", value: who(d.from_membership_id) },
                { label: "To", value: who(d.to_membership_id) },
                { label: "Budget", value: formatMicros(d.budget_micros, currency) },
                {
                  label: "Deadline",
                  value: d.deadline_at ? formatDateTime(d.deadline_at) : "None",
                },
                { label: "Sent", value: formatDateTime(d.created_at) },
                { label: "Depth", value: `${d.depth} (chain ${d.root_request_id.slice(-8)})` },
                ...(d.from_run_id
                  ? [
                      {
                        label: "From run",
                        value: (
                          <Link
                            to="/c/$slug/agents/runs/$id"
                            params={{ slug: company.slug, id: d.from_run_id }}
                            search={keepFocus}
                            className="text-primary-text underline-offset-4 hover:underline"
                          >
                            View run
                          </Link>
                        ),
                      },
                    ]
                  : []),
                ...(d.child_run_id
                  ? [
                      {
                        label: "Its run",
                        value: (
                          <Link
                            to="/c/$slug/agents/runs/$id"
                            params={{ slug: company.slug, id: d.child_run_id }}
                            search={keepFocus}
                            className="text-primary-text underline-offset-4 hover:underline"
                          >
                            View run
                          </Link>
                        ),
                      },
                    ]
                  : []),
                ...(d.task_id
                  ? [
                      {
                        label: "Task",
                        value: (
                          <Link
                            to="/c/$slug/work"
                            params={{ slug: company.slug }}
                            search={{ task: d.task_id } as never}
                            className="text-primary-text underline-offset-4 hover:underline"
                          >
                            Open the task
                          </Link>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </SettingsSection>
          <Section id="delegation-context" title="What it may read">
            {d.context_refs.length ? (
              <ul className="flex flex-wrap gap-2">
                {d.context_refs.map((c) => (
                  <li key={`${c.kind}:${c.id}`}>
                    <Badge variant="outline">
                      {c.kind} · {c.id.slice(-8)}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No context was handed over.</p>
            )}
          </Section>
          {d.result ? (
            <Section id="delegation-result" title="Handed back">
              <p className="text-sm">{d.result.summary ?? "No summary."}</p>
            </Section>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/** Decline (optional reason) or Complete (the summary handed back). */
function AnswerDialog({
  api,
  company,
  id,
  kind,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
  kind: "decline" | "complete";
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const field = useId();
  const answer = useMutation({
    mutationFn: () =>
      kind === "decline"
        ? api.client.delegationRequests.decline({
            params: { co: company.id, id },
            body: text.trim() ? { reason: text.trim() } : {},
          })
        : api.client.delegationRequests.complete({
            params: { co: company.id, id },
            body: { result: { summary: text.trim(), needs: "review" } },
          }),
    onSuccess: () => setOpen(false),
    onSettled: () => invalidateAgents(qc, company.id),
  });
  const decline = kind === "decline";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant={decline ? "outline" : "default"} size="lg" onClick={() => setOpen(true)}>
        {decline ? <XIcon aria-hidden="true" /> : <CheckIcon aria-hidden="true" />}
        {decline ? "Decline" : "Hand back"}
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{decline ? "Decline the request?" : "Hand the work back"}</DialogTitle>
          <DialogDescription>
            {decline
              ? "The requesting agent is woken with your answer."
              : "The requesting agent is woken with this summary."}
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={field}>{decline ? "Reason (optional)" : "Summary"}</FieldLabel>
          <Textarea id={field} value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
        {answer.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(answer.error, "The answer could not be saved.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button
            variant={decline ? "destructive" : "default"}
            disabled={answer.isPending || (!decline && !text.trim())}
            onClick={() => answer.mutate()}
          >
            {decline ? "Decline" : "Hand back"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
